import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';

const ROLE_META = {
  customer: { label: 'Customer', icon: '🛍️', color: '#B91C1C', dash: '/' },
  affiliate: { label: 'Affiliate', icon: '🤝', color: '#D97706', dash: '/affiliate/dashboard' },
  merchant: { label: 'Merchant', icon: '🏪', color: '#059669', dash: '/merchant/dashboard' },
  merchant_staff: { label: 'Staff', icon: '👥', color: '#0284C7', dash: '/merchant/dashboard' },
  admin: { label: 'Admin', icon: '⚙️', color: '#7C3AED', dash: '/admin/dashboard' },
  super_admin: { label: 'Super Admin', icon: '🛡️', color: '#1E293B', dash: '/super-admin/dashboard' },
  delivery: { label: 'Delivery', icon: '🚚', color: '#EA580C', dash: '/delivery/dashboard' },
  support: { label: 'Support', icon: '🎧', color: '#0891B2', dash: '/support/dashboard' },
  warehouse: { label: 'Warehouse', icon: '📦', color: '#65A30D', dash: '/warehouse/dashboard' },
  finance: { label: 'Finance', icon: '💰', color: '#16A34A', dash: '/finance/dashboard' },
  moderator: { label: 'Moderator', icon: '🔍', color: '#9333EA', dash: '/moderator/dashboard' },
  returns_manager: { label: 'Returns', icon: '🔄', color: '#DC2626', dash: '/returns-manager/dashboard' },
  marketing: { label: 'Marketing', icon: '📣', color: '#E11D48', dash: '/marketing/dashboard' },
  catalog_manager: { label: 'Catalog', icon: '📋', color: '#0D9488', dash: '/catalog/dashboard' },
  vendor_onboarding: { label: 'Onboarding', icon: '🤝', color: '#7C3AED', dash: '/vendor-onboarding/dashboard' },
  ad_manager: { label: 'Ads', icon: '🎯', color: '#BE185D', dash: '/ad-manager/dashboard' },
  api_partner: { label: 'API', icon: '🔑', color: '#1E293B', dash: '/api-partner/dashboard' },
  community_manager: { label: 'Community', icon: '🌐', color: '#2563EB', dash: '/community/dashboard' },
  dispute_arbitrator: { label: 'Disputes', icon: '⚖️', color: '#B45309', dash: '/disputes/dashboard' },
  qa_tester: { label: 'QA', icon: '✅', color: '#15803D', dash: '/qa-tester/dashboard' },
  regional_manager: { label: 'Regional', icon: '🗺️', color: '#4338CA', dash: '/regional/dashboard' },
};

const RoleSwitcher = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [canProxy, setCanProxy] = useState(false);
  const [proxyEmail, setProxyEmail] = useState('');
  const [showProxy, setShowProxy] = useState(false);
  const ref = useRef(null);

  // Check if currently in proxy mode
  const proxyInfo = localStorage.getItem('ebuywiz_proxy_original');
  const isProxying = !!proxyInfo;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowProxy(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (user) fetchRoles();
  }, [user?.id, user?.role]);

  const fetchRoles = async () => {
    try {
      const res = await api.get('/role-switch/my-roles');
      setRoles(res.data.data.availableRoles || []);
      setCanProxy(res.data.data.canProxy || false);
    } catch {
      // If endpoint fails, user has single role
      setRoles(user ? [user.role] : []);
    }
  };

  const handleSwitch = async (role) => {
    if (role === user.role) { setOpen(false); return; }
    try {
      const res = await api.put('/role-switch/switch', { role });
      const { token, user: updatedUser } = res.data.data;
      localStorage.setItem('ebuywiz_token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(updatedUser);
      toast.success(`Switched to ${ROLE_META[role]?.label || role}`);
      setOpen(false);
      navigate(ROLE_META[role]?.dash || '/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Switch failed');
    }
  };

  const handleProxy = async () => {
    if (!proxyEmail.trim()) return;
    try {
      // First find user by email
      const searchRes = await api.get('/support/orders', { params: { search: proxyEmail } });
      // Use admin user search instead
      const usersRes = await api.get('/admin/users', { params: { search: proxyEmail } });
      const targetUser = usersRes.data.data?.find(u => u.email === proxyEmail);
      if (!targetUser) { toast.error('Customer not found'); return; }

      const res = await api.post('/role-switch/proxy', { userId: targetUser.id });
      const { token, proxyUser, originalUser } = res.data.data;

      // Save original session for exit
      localStorage.setItem('ebuywiz_proxy_original', JSON.stringify({
        token: localStorage.getItem('ebuywiz_token'),
        userId: originalUser.id,
        name: `${originalUser.firstName} ${originalUser.lastName}`,
        role: originalUser.role,
      }));

      localStorage.setItem('ebuywiz_token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ ...proxyUser, isProxy: true });
      toast.success(`Now viewing as ${proxyUser.firstName} ${proxyUser.lastName}`);
      setOpen(false);
      setShowProxy(false);
      setProxyEmail('');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Proxy failed');
    }
  };

  const handleExitProxy = async () => {
    try {
      const original = JSON.parse(proxyInfo);
      const res = await api.post('/role-switch/exit-proxy', { originalUserId: original.userId });
      const { token, user: restored } = res.data.data;

      localStorage.removeItem('ebuywiz_proxy_original');
      localStorage.setItem('ebuywiz_token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(restored);
      toast.success('Proxy session ended');
      navigate(ROLE_META[restored.role]?.dash || '/');
    } catch (err) {
      // Fallback: restore from saved token
      const original = JSON.parse(proxyInfo);
      localStorage.removeItem('ebuywiz_proxy_original');
      localStorage.setItem('ebuywiz_token', original.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${original.token}`;
      window.location.reload();
    }
  };

  if (!user) return null;

  const activeRole = user.activeRole || user.role;
  const meta = ROLE_META[activeRole] || { label: activeRole, icon: '👤', color: '#6B7280' };
  const hasMultipleRoles = roles.length > 1;

  return (
    <>
      {/* Proxy Banner */}
      {isProxying && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: 'linear-gradient(90deg, #F59E0B, #D97706)',
          color: '#FFF', padding: '8px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
          fontSize: '13px', fontWeight: 600, fontFamily: "'Poppins', sans-serif",
          boxShadow: '0 2px 8px rgba(217,119,6,0.3)',
        }}>
          <span>👁️ Viewing as <strong>{user.firstName} {user.lastName}</strong> ({user.email})</span>
          <span style={{ opacity: 0.7 }}>|</span>
          <span style={{ opacity: 0.8 }}>Proxied by: {JSON.parse(proxyInfo).name}</span>
          <button
            onClick={handleExitProxy}
            style={{
              background: '#FFF', color: '#D97706', border: 'none', borderRadius: '6px',
              padding: '4px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', marginLeft: '8px',
            }}
          >
            ✕ Exit Proxy
          </button>
        </div>
      )}

      {/* Role Switcher Dropdown */}
      <div ref={ref} className="position-relative">
        <button
          onClick={() => setOpen(!open)}
          className="btn btn-sm d-flex align-items-center gap-2"
          style={{
            background: `rgba(255,255,255,0.15)`,
            color: '#FFF',
            border: `1.5px solid rgba(255,255,255,0.3)`,
            borderRadius: '8px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 700,
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{meta.icon}</span>
          <span>{meta.label}</span>
          {(hasMultipleRoles || canProxy) && (
            <span style={{ fontSize: '10px', opacity: 0.6, transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
          )}
        </button>

        {open && (hasMultipleRoles || canProxy) && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: '8px',
            width: '300px', background: '#FFF', borderRadius: '14px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.14)', border: '1px solid #E5E7EB',
            zIndex: 1000, overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Signed in as</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#1F2937' }}>{user.firstName} {user.lastName}</div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>{user.email}</div>
            </div>

            {/* Role List */}
            {hasMultipleRoles && (
              <div style={{ padding: '8px' }}>
                <div style={{ fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', padding: '6px 12px' }}>Switch Role</div>
                {roles.map(role => {
                  const rm = ROLE_META[role] || { label: role, icon: '👤', color: '#6B7280' };
                  const isActive = role === activeRole;
                  return (
                    <button
                      key={role}
                      onClick={() => handleSwitch(role)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 12px', border: 'none',
                        background: isActive ? `${rm.color}10` : 'transparent',
                        borderRadius: '8px', cursor: isActive ? 'default' : 'pointer',
                        textAlign: 'left', transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#F9FAFB'; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        width: '36px', height: '36px', borderRadius: '10px',
                        background: `${rm.color}12`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '18px', flexShrink: 0,
                      }}>
                        {rm.icon}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: rm.color }}>{rm.label}</div>
                        <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{role.replace(/_/g, ' ')}</div>
                      </div>
                      {isActive && (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, color: rm.color,
                          background: `${rm.color}15`, padding: '3px 8px', borderRadius: '12px',
                        }}>
                          ACTIVE
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Proxy Section */}
            {canProxy && (
              <div style={{ borderTop: '1px solid #F3F4F6', padding: '8px' }}>
                <div style={{ fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', padding: '6px 12px' }}>
                  👁️ Proxy as Customer
                </div>
                {!showProxy ? (
                  <button
                    onClick={() => setShowProxy(true)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', border: 'none', background: 'transparent',
                      borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FFF7ED'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: '#FEF3C7', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '18px',
                    }}>
                      👁️
                    </span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#D97706' }}>View as Customer</div>
                      <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Impersonate a customer account</div>
                    </div>
                  </button>
                ) : (
                  <div style={{ padding: '4px 12px 12px' }}>
                    <input
                      type="email"
                      placeholder="Enter customer email..."
                      value={proxyEmail}
                      onChange={(e) => setProxyEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleProxy()}
                      style={{
                        width: '100%', padding: '10px 12px', border: '2px solid #FDE68A',
                        borderRadius: '8px', fontSize: '13px', outline: 'none',
                        boxSizing: 'border-box', marginBottom: '8px',
                        fontFamily: "'Poppins', sans-serif",
                      }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={handleProxy}
                        style={{
                          flex: 1, padding: '8px', border: 'none', borderRadius: '6px',
                          background: '#D97706', color: '#FFF', fontSize: '12px', fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Start Proxy
                      </button>
                      <button
                        onClick={() => { setShowProxy(false); setProxyEmail(''); }}
                        style={{
                          padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '6px',
                          background: '#FFF', color: '#6B7280', fontSize: '12px', cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default RoleSwitcher;
